import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { getRoles } from '../tokenUtils';

export interface SearchState {
  currentPage: number;
  pageSize: number;
  sortBy: string;
  sortDescending: boolean;
  searchFilters: any;
  showAdvanced?: boolean;
}

/**
 * Persists search state (filters, pagination, sorting) in URL query parameters and sessionStorage.
 * URL params enable browser back/forward navigation, bookmarking, and sharing filtered views.
 * Auto-clears when navigating away from the route context.
 * 
 * @param routePrefix - The route prefix to track (e.g., 'members', 'scores', 'activities')
 * @param initialState - Default state to use on first load or after clearing
 * 
 * Priority order: URL params → sessionStorage → initial state
 * 
 * @example
 * const { searchState, setSearchState, clearSearchState } = useSearchState('members', {
 *   currentPage: 1,
 *   pageSize: 10,
 *   sortBy: 'CreatedUtc',
 *   sortDescending: true,
 *   searchFilters: {}
 * });
 * 
 * // Results in URL like: /admin/members?page=2&sortBy=LastName&firstName=John
 */
export function useSearchState(routePrefix: string, initialState: SearchState) {
  const location = useLocation();
  const navigate = useNavigate();
  const roles = getRoles();
  const rolePrefix = roles?.includes('Admin') ? 'admin' : 'member';
  const storageKey = `search:${rolePrefix}:${routePrefix}`;
  const contextKey = `search:${rolePrefix}:${routePrefix}:context`;
  const pathKey = `search:${rolePrefix}:${routePrefix}:path`;

  // Memoize initialState to prevent effect re-runs on every component render
  const memoizedInitialState = useMemo(() => initialState, [
    initialState.currentPage,
    initialState.pageSize,
    initialState.sortBy,
    initialState.sortDescending,
    initialState.showAdvanced
  ]);

  // Helper: Serialize state to URL query parameters
  const writeStateToURL = useCallback((state: SearchState, replace: boolean = true) => {
    const params = new URLSearchParams();
    
    // Add pagination (skip defaults to keep URL clean)
    if (state.currentPage && state.currentPage !== 1) {
      params.set('page', state.currentPage.toString());
    }
    if (state.pageSize && state.pageSize !== memoizedInitialState.pageSize) {
      params.set('pageSize', state.pageSize.toString());
    }
    
    // Add sorting (skip defaults)
    if (state.sortBy && state.sortBy !== memoizedInitialState.sortBy) {
      params.set('sortBy', state.sortBy);
    }
    if (state.sortDescending !== memoizedInitialState.sortDescending) {
      params.set('sortDesc', state.sortDescending.toString());
    }
    
    // Add advanced toggle (skip if false/default)
    if (state.showAdvanced) {
      params.set('showAdvanced', 'true');
    }
    
    // Add search filters (flatten object to individual params)
    if (state.searchFilters && typeof state.searchFilters === 'object') {
      Object.entries(state.searchFilters).forEach(([key, value]) => {
        if (value !== null && value !== undefined && value !== '') {
          params.set(key, String(value));
        }
      });
    }
    
    // Update URL without triggering navigation (replace to avoid history spam)
    const search = params.toString();
    
    // Track this state write to prevent sync effect from reading it back
    lastUrlState.current = JSON.stringify(state);
    
    navigate({ search }, { replace });
  }, [navigate, memoizedInitialState]);

  // Check if we're in the route context
  const isInRouteContext = useCallback((path: string) => {
    // More specific matching - path must start with the route prefix
    const pattern = new RegExp(`^/[^/]*/${routePrefix}(/|$)`);
    return pattern.test(path);
  }, [routePrefix]);

  // Extract the "route variant" - e.g., "/admin/members/status/active" or "/admin/scores/in-house"
  // keeps the status part, but "/admin/members/edit/123" is treated as just the base route
  const getRouteVariant = useCallback((path: string) => {
    // Match: /admin/scores, /admin/scores/in-house, /admin/scores/status/active
    // Exclude: /admin/scores/create, /admin/scores/[uuid]
    const uuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
    const match = path.match(new RegExp(`^(/[^/]*/${routePrefix}(?:/(?!create$|${uuidPattern}$)[^/]+)?)`));
    return match ? match[1] : path;
  }, [routePrefix]);

  // Check if this is a list page (not a detail page like edit/create)
  const isListPage = useCallback((path: string) => {
    // List pages: /admin/members, /admin/members/status/active, /admin/scores/in-house
    // Not list pages: /admin/members/edit/123, /admin/members/create, /admin/scores/[uuid]
    const uuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
    const listPattern = new RegExp(`^/[^/]*/${routePrefix}(?:/(?!create$|${uuidPattern}$)[^/]+)?$`);
    return listPattern.test(path);
  }, [routePrefix]);

  // Initialize state from URL -> sessionStorage -> initial values (priority order)
  const [searchState, setSearchState] = useState<SearchState>(() => {
    if (typeof window === 'undefined') return memoizedInitialState;
    
    try {
      const currentPath = location.pathname;
      
      // Helper to check route context (inline version for initializer)
      const pattern = new RegExp(`^/[^/]*/${routePrefix}(/|$)`);
      const currentInContext = pattern.test(currentPath);
      
      if (!currentInContext) {
        // Not in route context, clear and return initial
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(contextKey);
        sessionStorage.removeItem(pathKey);
        return memoizedInitialState;
      }
      
      const savedContext = sessionStorage.getItem(contextKey);
      const savedVariant = sessionStorage.getItem(pathKey);
      
      // Helper to get route variant (inline version for initializer)
      const uuidPattern = '[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}';
      const variantMatch = currentPath.match(new RegExp(`^(/[^/]*/${routePrefix}(?:/(?!create$|${uuidPattern}$)[^/]+)?)`));
      const currentVariant = variantMatch ? variantMatch[1] : currentPath;
      
      // Check if variant changed - if so, clear everything
      if (savedVariant && savedVariant !== currentVariant) {
        sessionStorage.removeItem(storageKey);
        sessionStorage.removeItem(pathKey);
        return memoizedInitialState;
      }
      
      // Priority 1: Read from URL (highest priority - enables bookmarking/sharing)
      // Use window.location.search directly (not the hook) since this runs during initialization
      const urlParams = new URLSearchParams(window.location.search);
      const urlState: Partial<SearchState> = {};
      
      // Parse pagination
      const page = urlParams.get('page');
      if (page) urlState.currentPage = parseInt(page, 10) || memoizedInitialState.currentPage;
      
      const pageSizeParam = urlParams.get('pageSize');
      if (pageSizeParam) urlState.pageSize = parseInt(pageSizeParam, 10) || memoizedInitialState.pageSize;
      
      // Parse sorting
      const sortByParam = urlParams.get('sortBy');
      if (sortByParam) urlState.sortBy = sortByParam;
      
      const sortDesc = urlParams.get('sortDesc');
      if (sortDesc !== null) urlState.sortDescending = sortDesc === 'true';
      
      // Parse advanced toggle
      const showAdv = urlParams.get('showAdvanced');
      if (showAdv !== null) urlState.showAdvanced = showAdv === 'true';
      
      // Parse search filters - collect all params that aren't pagination/sort/advanced
      const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
      const filters: any = {};
      let hasFilters = false;
      
      urlParams.forEach((value, key) => {
        if (!reservedParams.includes(key) && value) {
          filters[key] = value;
          hasFilters = true;
        }
      });
      
      if (hasFilters) {
        urlState.searchFilters = filters;
      }
      
      // Priority 2: Read from sessionStorage (fallback for non-URL fields)
      let storageState: Partial<SearchState> = {};
      if (savedContext === 'true' && (!savedVariant || savedVariant === currentVariant)) {
        const stored = sessionStorage.getItem(storageKey);
        if (stored) {
          storageState = JSON.parse(stored);
        }
      }
      
      // Merge: URL overrides storage, storage overrides defaults
      const mergedState = {
        ...memoizedInitialState,
        ...storageState,
        ...urlState,
      };
      
      return mergedState;
    } catch (error) {
      console.error('Error loading search state:', error);
    }
    
    return memoizedInitialState;
  });

  // Track the route variant in a ref to detect changes
  const currentVariant = getRouteVariant(location.pathname);
  const [trackedVariant, setTrackedVariant] = useState(currentVariant);
  
  // Track if this is the first render to avoid interfering with initial navigation
  const isFirstRender = useRef(true);
  
  // Track the last state we wrote to URL to avoid unnecessary syncs
  const lastUrlState = useRef<string>('');
  
  // Debounce timer for URL writes
  const urlWriteTimer = useRef<number | null>(null);
  
  // If variant changed, reset state immediately (before any renders with old data)
  if (currentVariant !== trackedVariant && isListPage(location.pathname)) {
    setTrackedVariant(currentVariant);
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(pathKey);
    
    // When variant changes (e.g., browser back from /members/status/active to /members),
    // read from URL instead of resetting to defaults to preserve search state
    const urlParams = new URLSearchParams(window.location.search);
    
    if (urlParams.toString()) {
      // Parse URL state
      const urlState: Partial<SearchState> = {};
      
      const page = urlParams.get('page');
      urlState.currentPage = page ? parseInt(page, 10) || memoizedInitialState.currentPage : memoizedInitialState.currentPage;
      
      const pageSizeParam = urlParams.get('pageSize');
      urlState.pageSize = pageSizeParam ? parseInt(pageSizeParam, 10) || memoizedInitialState.pageSize : memoizedInitialState.pageSize;
      
      const sortByParam = urlParams.get('sortBy');
      urlState.sortBy = sortByParam || memoizedInitialState.sortBy;
      
      const sortDesc = urlParams.get('sortDesc');
      urlState.sortDescending = sortDesc !== null ? sortDesc === 'true' : memoizedInitialState.sortDescending;
      
      const showAdv = urlParams.get('showAdvanced');
      urlState.showAdvanced = showAdv !== null ? showAdv === 'true' : memoizedInitialState.showAdvanced;
      
      const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced'];
      const filters: any = {};
      urlParams.forEach((value, key) => {
        if (!reservedParams.includes(key) && value) {
          filters[key] = value;
        }
      });
      urlState.searchFilters = filters;
      
      const newState = { ...memoizedInitialState, ...urlState };
      if (JSON.stringify(searchState) !== JSON.stringify(newState)) {
        setSearchState(newState);
      }
    } else {
      // No URL params, reset to defaults
      if (JSON.stringify(searchState) !== JSON.stringify(memoizedInitialState)) {
        setSearchState(memoizedInitialState);
      }
    }
  }

  // Mark that we're in this context and track the route variant (only for list pages)
  useEffect(() => {
    const currentInContext = isInRouteContext(location.pathname);
    if (currentInContext) {
      sessionStorage.setItem(contextKey, 'true');
      
      // Only update the saved variant if we're on a list page
      // Detail pages (edit/create) should not change the tracking
      if (isListPage(location.pathname)) {
        const variant = getRouteVariant(location.pathname);
        sessionStorage.setItem(pathKey, variant);
      }
    } else {
      sessionStorage.removeItem(contextKey);
      sessionStorage.removeItem(pathKey);
      sessionStorage.removeItem(storageKey);
    }
    
    // Cleanup on unmount: check if we're leaving the route context
    return () => {
      // Use current location from window in case React Router hasn't updated yet
      const nextPath = window.location.pathname;
      const stillInContext = isInRouteContext(nextPath);
      
      if (!stillInContext) {
        sessionStorage.removeItem(contextKey);
        sessionStorage.removeItem(pathKey);
        sessionStorage.removeItem(storageKey);
      }
    };
  }, [location.pathname, contextKey, pathKey, storageKey, isInRouteContext, isListPage, getRouteVariant]);

  // Sync state when URL changes (handles browser back/forward navigation)
  useEffect(() => {
    // Skip first render to avoid interfering with initial load
    if (isFirstRender.current) return;
    
    // Only sync on list pages
    if (!isListPage(location.pathname)) return;
    
    // Parse current URL params
    const urlParams = new URLSearchParams(window.location.search);
    
    // Parse current URL state
    const urlState: Partial<SearchState> = {};
    
    // Parse pagination
    const page = urlParams.get('page');
    urlState.currentPage = page ? parseInt(page, 10) || memoizedInitialState.currentPage : memoizedInitialState.currentPage;
    
    const pageSizeParam = urlParams.get('pageSize');
    urlState.pageSize = pageSizeParam ? parseInt(pageSizeParam, 10) || memoizedInitialState.pageSize : memoizedInitialState.pageSize;
    
    // Parse sorting
    const sortByParam = urlParams.get('sortBy');
    urlState.sortBy = sortByParam || memoizedInitialState.sortBy;
    
    const sortDesc = urlParams.get('sortDesc');
    urlState.sortDescending = sortDesc !== null ? sortDesc === 'true' : memoizedInitialState.sortDescending;
    
    // Parse advanced toggle
    const showAdv = urlParams.get('showAdvanced');
    urlState.showAdvanced = showAdv !== null ? showAdv === 'true' : memoizedInitialState.showAdvanced;
    
    // Parse search filters
    const reservedParams = ['page', 'pageSize', 'sortBy', 'sortDesc', 'showAdvanced', 'view'];
    const filters: any = {};
    
    urlParams.forEach((value, key) => {
      if (!reservedParams.includes(key) && value) {
        filters[key] = value;
      }
    });
    
    urlState.searchFilters = filters;
    
    // Create merged state
    const mergedState = {
      ...searchState,
      ...urlState,
    };
    
    // Check if this matches what we last wrote (prevent reading back our own writes)
    const mergedStateJson = JSON.stringify(mergedState);
    if (lastUrlState.current === mergedStateJson) {
      return; // This is our own write, don't sync back
    }
    
    // Check if URL is actually different from current state
    const currentStateJson = JSON.stringify(searchState);
    if (mergedStateJson === currentStateJson) {
      return; // No actual change
    }
    
    // URL changed from browser back/forward, update state
    setSearchState(mergedState);
  }, [location.search, location.pathname, isListPage, memoizedInitialState]);

  // Persist state to sessionStorage whenever it changes (but only for matching variant)
  useLayoutEffect(() => {
    if (typeof window === 'undefined') return;
    
    // Skip first render to avoid interfering with initial navigation
    if (isFirstRender.current) {
      isFirstRender.current = false;
      return;
    }
    
    // Only save if we're on a list page
    if (!isListPage(location.pathname)) return;
    
    const currentVariant = getRouteVariant(location.pathname);
    const savedVariant = sessionStorage.getItem(pathKey);
    
    // Only persist if this is the current variant (or first visit)
    if (!savedVariant || savedVariant === currentVariant) {
      try {
        // Persist to sessionStorage (performance cache)
        sessionStorage.setItem(storageKey, JSON.stringify(searchState));
      } catch (error) {
        console.error('Error saving search state:', error);
      }
    }
  }, [searchState, storageKey, pathKey, location.pathname, isListPage, getRouteVariant]);

  // Helper to clear search state (clears both sessionStorage and URL)
  const clearSearchState = () => {
    sessionStorage.removeItem(pathKey);
    sessionStorage.removeItem(storageKey);
    sessionStorage.removeItem(contextKey);
    setSearchState(memoizedInitialState);
    
    // Clear URL params
    navigate({ search: '' }, { replace: true });
  };

  // Helper functions to update individual parts of the search state
  const updateSearchFilters = (filters: any) => {
    setSearchState(prev => ({ ...prev, searchFilters: filters }));
  };

  const updateCurrentPage = (page: number) => {
    const newState = { ...searchState, currentPage: page };
    setSearchState(newState);
    // Write to URL immediately since pagination triggers immediate search
    writeStateToURL(newState, true);
  };

  const updatePageSize = (size: number) => {
    const newState = { ...searchState, pageSize: size };
    setSearchState(newState);
    // Write to URL immediately since page size change triggers immediate search
    writeStateToURL(newState, true);
  };

  const updateSortBy = (field: string) => {
    const newState = { ...searchState, sortBy: field };
    setSearchState(newState);
    // Write to URL immediately since sorting triggers immediate search
    writeStateToURL(newState, true);
  };

  const updateSortDescending = (descending: boolean) => {
    const newState = { ...searchState, sortDescending: descending };
    setSearchState(newState);
    // Write to URL immediately since sorting triggers immediate search
    writeStateToURL(newState, true);
  };

  const setSort = (field: string, descending: boolean, page: number = 1) => {
    const newState = { ...searchState, sortBy: field, sortDescending: descending, currentPage: page };
    setSearchState(newState);
    // Write to URL immediately since sorting triggers immediate search
    writeStateToURL(newState, true);
  };

  const updateShowAdvanced = (show: boolean) => {
    const newState = { ...searchState, showAdvanced: show };
    setSearchState(newState);
    // Write to URL immediately to persist the basic/advanced toggle
    writeStateToURL(newState, true);
  };

  type CommitSearchOptions = {
    page?: number;
    showAdvanced?: boolean;
    replace?: boolean;
  };

  const commitSearch = (filters: any, options?: CommitSearchOptions) => {
    const newState: SearchState = {
      ...searchState,
      currentPage: options?.page ?? 1,
      showAdvanced: options?.showAdvanced ?? searchState.showAdvanced,
      searchFilters: filters,
    };

    setSearchState(newState);
    writeStateToURL(newState, options?.replace ?? true);
  };

  return {
    // State values
    currentPage: searchState.currentPage,
    pageSize: searchState.pageSize,
    sortBy: searchState.sortBy,
    sortDescending: searchState.sortDescending,
    searchFilters: searchState.searchFilters,
    showAdvanced: searchState.showAdvanced,
    
    // Setters
    setCurrentPage: updateCurrentPage,
    setPageSize: updatePageSize,
    setSortBy: updateSortBy,
    setSortDescending: updateSortDescending,
    setSort,
    setSearchFilters: updateSearchFilters,
    setShowAdvanced: updateShowAdvanced,
    commitSearch,
    
    // Full state access
    searchState,
    setSearchState,
    clearSearchState,
  };
}
